/**
 * Workbench data seam — one indirection between the pages and their data source.
 *
 * Live first, and in production live ONLY:
 *   - The queue, the case review, the add-in's stored case and the ops dashboard
 *     read the live API through the `@/lib/api` hooks (`useQueue`, `useCase`,
 *     `useCaseByMessage`, `useOpsMetrics`). The queue is server-driven: filters,
 *     search and paging are query parameters, and the seam returns one page plus
 *     the totals.
 *   - The typed mock fixtures in `@/lib/mock` are a LOCAL DEV convenience only:
 *     they are served when `import.meta.env.DEV` is true and the live source is
 *     empty or failing. In a production build `DEV` is statically `false`, so the
 *     fallback branch (and the fixtures) are dropped; an empty store renders the
 *     page's empty state and a failure renders its error state. The deployed app
 *     never shows demo data.
 *
 * Wire → UI mapping: the API speaks snake_case with engine-native values
 * (`line_count`, `rfq_quote`, confidence 0..1, preview lines without per-line
 * confidence); the pages render the UI model in `@/lib/mock/types` (camelCase,
 * confidence 0..100, closed enums). Every live payload crosses that boundary here,
 * treated as untrusted: unknown enum values become null rather than crashing a
 * lookup table. Confidence goes through `toConfidencePercent`, which passes
 * values already `> 1` through untouched, so nothing is double-scaled.
 *
 * Read-only throughout — the hooks only read the local case store and metrics.
 */

import type { UseQueryResult } from "@tanstack/react-query";

import {
  type AttachmentRead,
  type AttentionKind,
  type CaseBlocker,
  type CaseGroundTruth,
  type CaseOrigin,
  type CaseQuotation,
  type CaseWork,
  type CloseCandidate,
  type ClosedBy,
  type ColleagueNote,
  type CustomerBlock,
  type CustomerCandidate,
  type CustomerEvidence,
  type CustomerHeaderDefault,
  type CustomerIntercompany,
  type EarlierMention,
  type Executive,
  type ExecutiveWindow,
  type LineCandidate,
  type LineDecision,
  type LineOverride,
  type LineSource,
  type LineStatus,
  type LiveQuoteCaseDetail,
  type LiveQuoteCaseSummary as WireSummary,
  type LiveQuoteLine,
  type MatchingInvestigationSummary,
  type MatchingSourceEvidence,
  type OpsMetrics,
  type OpsWindow,
  type OrderCheck,
  type OtherEntityItem,
  type QueueBacklog,
  type QueueCounts,
  type QueueFilters,
  type QuoteFollowUp,
  type QuoteHistoryEvidence,
  type RelatedCase,
  type SuggestedCustomer,
  toConfidencePercent,
  toExecutive,
  useCase,
  useCaseByMessage,
  useExecutiveMetrics,
  useOpsMetrics,
  useQueue,
  type UsualOwner,
  type WorkStatus,
} from "@/lib/api";
import {
  INQUIRY_DOWNGRADED,
  INQUIRY_DOWNGRADED_WHY,
  intentLabel,
  isDowngradedInquiry,
} from "@/lib/intents";
import { logger } from "@/lib/logger";
import {
  type AreaCode,
  type BrandKey,
  type CaseField,
  type CaseIntent,
  type CaseInvestigation,
  type CaseState,
  type DemandMode,
  type ExceptionKind,
  getCase,
  getExecutiveFixture,
  getOpsMetrics,
  getQueue,
  getWireCase,
  getWireQueue,
  type InvestigationProposal,
  type InvestigationStep,
  type LinePrice,
  type LineProvisional,
  type LineState,
  type MailSegment,
  type PreflightChip,
  type QuoteCaseDetail,
  type QuoteCaseSummary,
  type QuoteLine,
  type SignalTone,
  type TwinDecision,
} from "@/lib/mock";
import { isPictureAttachment, toPhotoReading } from "@/lib/photo-reading";
import { toCaseRules } from "@/lib/rules";
import { manualPriceEvidenceText, priceEvidenceText } from "@/lib/work-labels";

/** True only in the Vite dev server; statically `false` in a production build. */
const DEV_FIXTURES = import.meta.env.DEV;

/** The stable shape every seam hook returns, mirroring a TanStack Query result. */
export interface WorkbenchData<T> {
  data: T;
  isLoading: boolean;
  isError: boolean;
  /** True once a live source backs this seam and returned data. */
  isLive: boolean;
  /** Epoch ms of the last successful live fetch (0 = never). */
  updatedAt: number;
  /** The live query's error, if any (an `ApiError` for HTTP failures). */
  error: unknown;
}

// ─── enum normalisation (untrusted wire strings → closed UI unions) ─────────

const BRANDS: readonly BrandKey[] = [
  "disa",
  "wheelabrator",
  "strikowestofen",
  "simpson",
  "monitizer",
];
const MODES: readonly DemandMode[] = ["breakdown", "planned", "admin"];
const STATES: readonly CaseState[] = [
  "queued",
  "processing",
  "needs-review",
  "draft-ready",
  "in-progress",
  "not-an-rfq",
  "parked",
  "duplicate",
  "auto-closed",
];
const EXCEPTIONS: readonly ExceptionKind[] = [
  "no-price",
  "uom",
  "credit-hold",
  "duplicate-po",
  "expired-quote",
];
const SIGNAL_TONES: readonly SignalTone[] = ["success", "warning", "danger", "info", "neutral"];
const ORIGINS: readonly CaseOrigin[] = ["customer", "internal", "portal"];
const LINE_STATUSES: readonly LineStatus[] = ["matched", "suggested", "open", "failed"];
const REASON_CODES = [
  "thread_carry",
  "exact_suggested",
  "alias_suggested",
  "content_filter",
  "customer_open",
  "customer_open_search",
  "scan_read",
  "photo_read",
  "successor",
  "code_sweep",
  "code_repair",
  "thread_sweep",
  "quote_carry",
  "learned_correction",
  "similar_description",
  "investigation_suggested",
  "recovered_request",
  // Hunt round 2 (Q-28 a): the real reason a line has no item, or does not fit.
  "other_entity",
  "not_in_catalogue",
  "typo_candidate",
  "blocked_item",
  "mismatch",
] as const;
const MISMATCHES = ["size", "description", "customer_says_wrong", "assembly_for_position"] as const;
const HSN_STATUSES = ["present", "missing", "placeholder", "malformed", "not_known"] as const;
/** Most other-entity hints on one line (CONTRACTS.md 3.3). */
const MAX_OTHER_ENTITY = 3;
const WORK_STATUSES: readonly WorkStatus[] = [
  "open",
  "replied",
  "waiting_customer",
  "done",
  "not_for_us",
  "auto_closed",
  "no_activity",
];
const WORK_ROW_STATUSES: readonly CaseWork["status"][] = [
  "open",
  "done",
  "not_for_us",
  "waiting_customer",
];
const CLOSED_KINDS: readonly ClosedBy["kind"][] = [
  "quotation_sent",
  "order_confirmation_sent",
  "rep_reply",
  "fo_quotation",
  "desk_done",
  "desk_deleted",
  "own_reply",
  "thread_reply",
  "thread_head_closed",
];
// Close hints: the two rules of 10 Oct 2026 and the shadow auto-close of 11 Oct 2026.
const CLOSE_CANDIDATE_KINDS: readonly CloseCandidate["kind"][] = [
  "no_activity",
  "fo_quotation",
  "desk_done",
  "desk_deleted",
  "own_reply",
  "thread_reply",
  "thread_head_closed",
  // Hunt round 2, decision 8: a notification or a newsletter; an offer, never applied.
  "not_an_inquiry",
];
const DECISION_KINDS: readonly LineDecision["kind"][] = ["confirm", "item", "no_item"];
const PRICE_STATUSES: readonly LinePrice["status"][] = [
  "found",
  "no_item",
  "no_customer",
  "no_price_group",
  "no_price",
  "expired",
  "currency_ambiguous",
  "other_entity",
  "not_loaded",
  "unavailable",
  // Sales review of 12 Oct 2026 (P-01): the status was dropped here, so a line without
  // a quantity had no price block and its cell could not say why it has no price.
  "no_quantity",
];
const MARGIN_STATUSES = ["ok", "needs_approval", "not_checked"] as const;
const ITEM_STATUSES = ["ok", "blocked", "phase_out", "service"] as const;
const CUSTOMER_ROLES = [
  "customer",
  "agent",
  "norican_colleague",
  "portal",
  "sister_company",
  "unknown",
] as const;
const CANDIDATE_KINDS: readonly NonNullable<CustomerCandidate["kind"]>[] = [
  "sibling",
  "end_customer",
  "agent",
  "history",
  "name_match",
  "intercompany",
  "invoice_family",
  "quote_ref",
  "ship_to",
  "redirect",
  "contact_memory",
  "domain_memory",
  "investigation",
  "alias",
  "twin",
];
const INTERCOMPANY_PROOFS = ["exchange-internal", "trusted-relay"] as const;
const SOURCE_METHODS = ["text", "xlsx", "docx", "type3", "image"] as const;
const ORDER_COVERAGES: readonly OrderCheck["coverage"][] = ["lines", "header_only", "none"];
/** Most customer candidates shown for one case. */
const MAX_CUSTOMER_CANDIDATES = 8;
/** Most evidence sentences shown for the customer of one case, and their length. */
const MAX_CUSTOMER_EVIDENCE = 4;
const MAX_EVIDENCE_CHARS = 160;
const MAX_BUYER_NAME_CHARS = 120;
const RELATIONS: readonly RelatedCase["relation"][] = [
  "first_mail",
  "reply",
  "copy",
  "copy_of",
  "other_mailbox",
  "same_inquiry",
  "same_inquiry_as",
];
const QUOTATION_SOURCES: readonly CaseQuotation["source"][] = [
  "subject",
  "body",
  "po_reference",
  "sent_document",
  "fo_contact",
  "follow_up",
];
const QUOTATION_STATUSES: readonly CaseQuotation["status"][] = [
  "created",
  "lost",
  "sent",
  "confirmed",
  "cancelled",
  "unknown",
];
/** Most "did you mean" items shown for one line. */
const MAX_SUGGESTIONS = 5;

/** The fixed sentence for a line the engine could not evaluate (never raw error text). */
const LINE_FAILED_SENTENCE = "I2Q could not evaluate this line. It needs a manual check.";
/** The fixed sentence for a mail the engine could not process (never raw error text). */
const ENGINE_ERROR_SENTENCE = "I2Q could not process this mail.";

/** Raw engine / transport error text that must never reach a reviewer. */
const RAW_ERROR = /llm error|HTTP Error/i;

function oneOf<T extends string>(values: readonly T[], raw: unknown): T | null {
  if (typeof raw !== "string") {
    return null;
  }
  const lowered = raw.trim().toLowerCase();
  return (values as readonly string[]).includes(lowered) ? (lowered as T) : null;
}

/** Engine intent (`rfq_quote`, `order_place`, …) or an already-UI value → `CaseIntent`. */
function toIntent(raw: unknown): CaseIntent | null {
  if (typeof raw !== "string" || raw.trim() === "") {
    return null;
  }
  switch (raw.trim().toLowerCase()) {
    case "rfq":
    case "rfq_quote":
      return "rfq";
    case "price-indication":
      return "price-indication";
    case "follow-up":
    case "order_status":
      return "follow-up";
    case "order":
    case "order_place":
      return "order";
    default:
      return "other";
  }
}

/** The area of a case as the wire sends it (an entity code), lower case; null without one. */
function toArea(raw: unknown): AreaCode | null {
  if (typeof raw !== "string") {
    return null;
  }
  const code = raw.trim().toLowerCase();
  return /^[a-z]{2,4}$/.test(code) && code !== "none" ? code : null;
}

function toState(raw: unknown, id: string): CaseState {
  const state = oneOf(STATES, raw);
  if (state) {
    return state;
  }
  logger.warn("[data] unknown case state, showing as needs-review", { id, state: raw });
  return "needs-review";
}

/** A lifecycle state from an untrusted wire string; an unknown one reads as needs-review. */
export function toCaseState(raw: unknown): CaseState {
  return oneOf(STATES, raw) ?? "needs-review";
}

function toTone(raw: unknown, fallback: SignalTone): SignalTone {
  return oneOf(SIGNAL_TONES, raw) ?? fallback;
}

function toFieldTone(raw: unknown): CaseField["tone"] {
  const tone = toTone(raw, "success");
  return tone === "warning" || tone === "danger" ? tone : "success";
}

/** ISO timestamp → a short local "dd Mon, HH:MM"; anything else passes through. */
function formatWhen(raw: string | undefined): string {
  if (!raw) {
    return "";
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ─── wire → UI mappers ──────────────────────────────────────────────────────

function toUiSummary(row: WireSummary): QuoteCaseSummary {
  const state = toState(row.state, row.id);
  // Classified as an inquiry, then turned down by the full-text read: name both readings.
  const downgraded = isDowngradedInquiry(state, row.intent);
  return {
    id: row.id,
    ref: row.ref || row.id,
    customer: row.customer,
    customerStandIn: row.customer_stand_in === true,
    brand: oneOf(BRANDS, row.brand),
    area: toArea(row.area),
    mode: oneOf(MODES, row.mode),
    intent: toIntent(row.intent),
    lineCount: row.line_count ?? null,
    confidence: row.confidence == null ? null : toConfidencePercent(row.confidence),
    exceptions: (row.exceptions ?? [])
      .map((e) => oneOf(EXCEPTIONS, e))
      .filter((e): e is ExceptionKind => e !== null),
    assignee: row.assignee ?? null,
    ageMinutes: Math.max(0, row.age_minutes ?? 0),
    state,
    receivedAt: row.received_at ?? "",
    subject: row.subject ?? null,
    senderName: row.sender_name ?? null,
    senderEmail: row.sender_email ?? null,
    mailbox: row.mailbox ?? null,
    route: row.route ?? null,
    origin: oneOf(ORIGINS, row.origin) ?? "customer",
    linesAuto: row.lines_auto ?? null,
    reviewReason: row.review_reason ?? (downgraded ? INQUIRY_DOWNGRADED_WHY : null),
    replyFound: row.reply_found ?? null,
    intentLabel: downgraded ? INQUIRY_DOWNGRADED : intentLabel(row.intent),
    engineIntent: row.intent ?? null,
    isReply: row.is_reply === true,
    followUpOf: typeof row.follow_up_of === "string" ? row.follow_up_of : null,
    duplicateOf: typeof row.duplicate_of === "string" ? row.duplicate_of : null,
    threadCount: toCount(row.thread_count),
    duplicateCount: toCount(row.duplicate_count),
    workStatus: oneOf(WORK_STATUSES, row.work_status),
    closedKind: oneOf(CLOSED_KINDS, row.closed_kind),
    closedAt: text(row.closed_at),
    closedDecidedAt: text(row.closed_decided_at),
    ruleFlagGroups: Array.isArray(row.rule_flag_groups)
      ? row.rule_flag_groups
          .filter((g) => typeof g?.label === "string" && typeof g.count === "number" && g.count > 0)
          .map((g) => ({
            label: g.label,
            count: Math.floor(g.count),
            severity: g.severity === "blocking" ? ("blocking" as const) : ("warning" as const),
          }))
      : [],
    addressedTo: Array.isArray(row.addressed_to)
      ? [
          ...new Set(
            row.addressed_to.map((address) => text(address)).filter((a): a is string => !!a),
          ),
        ]
      : [],
    customerRole: oneOf(CUSTOMER_ROLES, row.customer_role),
    ruleNote:
      typeof row.rule_note === "string" && row.rule_note.trim() !== "" ? row.rule_note : null,
    ruleFlagCount:
      typeof row.rule_flag_count === "number" && row.rule_flag_count > 0
        ? Math.floor(row.rule_flag_count)
        : 0,
    linesFound:
      typeof row.lines_found === "number" && row.lines_found >= 0
        ? Math.floor(row.lines_found)
        : null,
    alsoReceivedIn: Array.isArray(row.also_received_in)
      ? [
          ...new Set(
            row.also_received_in.map((mailbox) => text(mailbox)).filter((m): m is string => !!m),
          ),
        ]
      : [],
    customerToConfirm:
      typeof row.customer_to_confirm === "boolean" ? row.customer_to_confirm : null,
    suggestedCustomers: toSuggestedCustomers(row.suggested_customers),
    replyDraftReady: row.reply_draft_ready === true,
    canFinishNow: row.can_finish_now === true,
    forwardedBy: text(row.forwarded_by),
    foldedReplies: toCount(row.folded_replies),
    attention: Array.isArray(row.attention)
      ? [...new Set(row.attention.map((kind) => oneOf(ATTENTION_KINDS, kind)))].filter(
          (kind): kind is AttentionKind => kind !== null,
        )
      : [],
    workingDaysOpen:
      typeof row.working_days_open === "number" && row.working_days_open >= 0
        ? Math.floor(row.working_days_open)
        : null,
    closeCandidate: toCloseCandidate(row.close_candidate),
    usualOwner: toUsualOwner(row.usual_owner),
    nearDuplicateOf: text(row.near_duplicate_of),
    nearDuplicateText: text(row.near_duplicate_text),
    chaser: row.chaser === true,
    couldNotProcess: row.could_not_process === true,
  };
}

/** The usual owner of the customer's quotations; null without a mail. */
function toUsualOwner(raw: unknown): UsualOwner | null {
  if (!isRecord(raw)) {
    return null;
  }
  const email = text(raw.email)?.toLowerCase() ?? null;
  if (!email || !email.includes("@")) {
    return null;
  }
  const quotations =
    typeof raw.quotations === "number" ? Math.max(0, Math.floor(raw.quotations)) : 0;
  const total =
    typeof raw.total === "number" ? Math.max(quotations, Math.floor(raw.total)) : quotations;
  return { email, name: text(raw.name), quotations, total };
}

const ATTENTION_KINDS: readonly AttentionKind[] = [
  "back_after_customer",
  "back_from_waiting",
  "over_1_working_day",
  "customer_chasing",
];

/** At most two suggested customers, each with an account. */
const SUGGESTION_STRENGTHS = ["certain", "evidence", "guess"] as const;

function toSuggestedCustomers(raw: unknown): SuggestedCustomer[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(isRecord)
    .map((entry) => ({
      account: text(entry.account) ?? "",
      name: text(entry.name),
      city: text(entry.city),
      postcode: text(entry.postcode),
      // Unknown or missing reads as a guess: no one-click confirmation (P13).
      strength: oneOf(SUGGESTION_STRENGTHS, entry.strength) ?? "guess",
    }))
    .filter((entry) => entry.account !== "")
    .slice(0, 2);
}

/** A close hint with a sentence; anything else is dropped. */
function toCloseCandidate(raw: unknown): CloseCandidate | null {
  if (!isRecord(raw)) {
    return null;
  }
  const kind = oneOf(CLOSE_CANDIDATE_KINDS, raw.kind);
  const sentence = text(raw.sentence);
  if (!kind || !sentence) {
    return null;
  }
  return { kind, sentence, doc_number: text(raw.doc_number), applied: raw.applied === true };
}

// ─── stage 2 mappers (every field optional; an older payload maps to null / []) ──

/** A non-empty trimmed string, else null. */
function text(raw: unknown): string | null {
  return typeof raw === "string" && raw.trim() !== "" ? raw.trim() : null;
}

/** A finite number, else null. */
function num(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

/** A decimal as the wire sends it (string, or a number from an older payload), else null. */
function decimal(raw: unknown): string | null {
  return typeof raw === "number" && Number.isFinite(raw) ? String(raw) : text(raw);
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw != null && typeof raw === "object" && !Array.isArray(raw);
}

/** The work row of a case; null when the server sent none (a backend before stage 2). */
function toWork(raw: unknown): CaseWork | null {
  if (!isRecord(raw)) {
    return null;
  }
  return {
    status: oneOf(WORK_ROW_STATUSES, raw.status) ?? "open",
    status_by: text(raw.status_by),
    status_at: text(raw.status_at),
    status_note: text(raw.status_note),
    reopened_at: text(raw.reopened_at),
    assignee: text(raw.assignee),
    assigned_at: text(raw.assigned_at),
    customer_account: text(raw.customer_account),
    customer_by: text(raw.customer_by),
    customer_at: text(raw.customer_at),
    version: Math.max(0, Math.floor(num(raw.version) ?? 0)),
  };
}

function toClosedBy(raw: unknown): ClosedBy | null {
  if (!isRecord(raw)) {
    return null;
  }
  const kind = oneOf(CLOSED_KINDS, raw.kind);
  return kind ? { kind, doc_number: text(raw.doc_number), at: text(raw.at) ?? "" } : null;
}

/** A rep's decision on a line; anything without a key and a known kind is dropped. */
function toDecision(raw: unknown): LineDecision | null {
  if (!isRecord(raw)) {
    return null;
  }
  const kind = oneOf(DECISION_KINDS, raw.kind);
  const lineKey = text(raw.line_key);
  if (!kind || !lineKey) {
    return null;
  }
  return {
    line_key: lineKey,
    kind,
    item: text(raw.item),
    item_area: text(raw.item_area)?.toLowerCase() ?? null,
    item_name: text(raw.item_name),
    decided_by: text(raw.decided_by) ?? "",
    decided_at: text(raw.decided_at) ?? "",
    engine_changed: raw.engine_changed === true,
    ...(text(raw.enquiry_line) ? { enquiry_line: text(raw.enquiry_line) as string } : {}),
    ...(raw.by_i2q === true ? { by_i2q: true } : {}),
  };
}

/** The first mail's decision on the same line of a twin (P18); null without a case id. */
function toTwinDecision(raw: unknown): TwinDecision | null {
  const decision = toDecision(raw);
  const caseId = isRecord(raw) ? text(raw.case_id) : null;
  return decision && caseId ? { ...decision, case_id: caseId } : null;
}

const OVERRIDE_KINDS = ["edit", "remove", "add"] as const;

/**
 * A correction sales made to a line (F08), with the price a person set (round 4);
 * anything without a known kind is dropped.
 */
export function toLineOverride(raw: unknown): LineOverride | null {
  if (!isRecord(raw)) {
    return null;
  }
  const kind = oneOf(OVERRIDE_KINDS, raw.kind);
  if (!kind) {
    return null;
  }
  return {
    kind,
    qty: num(raw.qty),
    unit: text(raw.unit),
    engine_qty: num(raw.engine_qty),
    engine_unit: text(raw.engine_unit),
    engine_changed: raw.engine_changed === true,
    note: text(raw.note),
    decided_by: text(raw.decided_by) ?? "",
    decided_at: text(raw.decided_at) ?? "",
    unit_price: decimal(raw.unit_price),
    currency: text(raw.currency),
    price_note: text(raw.price_note),
    price_by: text(raw.price_by),
    price_at: text(raw.price_at),
    engine_unit_price: decimal(raw.engine_unit_price),
    engine_currency: text(raw.engine_currency),
  };
}

/**
 * The suffix of the key of a decision that matches no line after a re-read
 * (`REKEY_ORPHAN_SUFFIX` in `backend/src/rfq_foundry/store/records.py`, hunt round 3, H3).
 */
export const ORPHAN_KEY_SUFFIX = "~orphan";

/** True for the key of a decision without a line: the page never writes on such a key. */
export function isOrphanKey(key: string | null | undefined): boolean {
  return typeof key === "string" && key.endsWith(ORPHAN_KEY_SUFFIX);
}

/** A line key the page may write on; null for no key or the key of an orphan. */
function writableLineKey(raw: unknown): string | null {
  const key = text(raw);
  return key && !isOrphanKey(key) ? key : null;
}

function toDecisions(raw: unknown): LineDecision[] {
  return Array.isArray(raw) ? raw.map(toDecision).filter((d): d is LineDecision => d !== null) : [];
}

/**
 * Parts named only in an earlier message of the mail (bug hunt 14 Oct 2026, Q-01 and
 * Q-02). An entry without the written words is dropped; a quantity must be a positive
 * number.
 */
function toEarlierMentions(raw: unknown): EarlierMention[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.flatMap((entry): EarlierMention[] => {
    if (!isRecord(entry)) {
      return [];
    }
    const written = text(entry.written);
    if (!written) {
      return [];
    }
    const qty = num(entry.qty);
    return [
      {
        written,
        item: text(entry.item),
        name: text(entry.name),
        qty: qty != null && qty > 0 ? qty : null,
        ordered: text(entry.ordered),
      },
    ];
  });
}

/** Where a line was read from: an attachment needs a whole-number index and a file name. */
function toLineSource(raw: unknown): LineSource | null {
  if (!isRecord(raw)) {
    return null;
  }
  if (raw.kind === "mail") {
    return raw.quoted === true ? { kind: "mail", quoted: true } : { kind: "mail" };
  }
  if (raw.kind === "thread") {
    return { kind: "thread" };
  }
  // A position of our quotation (amend, or an order on it: decision 1 of 14 Oct 2026).
  if (raw.kind === "quotation") {
    return { kind: "quotation", doc_number: text(raw.doc_number), line_no: text(raw.line_no) };
  }
  const index = num(raw.index);
  const file = text(raw.file);
  if (
    raw.kind !== "attachment" ||
    index == null ||
    !Number.isInteger(index) ||
    index < 0 ||
    !file
  ) {
    return null;
  }
  return {
    kind: "attachment",
    index,
    file,
    page: num(raw.page),
    sheet: text(raw.sheet),
    method: oneOf(SOURCE_METHODS, raw.method) ?? undefined,
    doc_type: text(raw.doc_type) ?? undefined,
    doc_number: text(raw.doc_number),
    line_no: text(raw.line_no),
    part: wholeFrom(raw.part, 1, 16),
    parts: wholeFrom(raw.parts, 1, 16),
    region: text(raw.region),
    // Hunt round 2: a picture in the mail body, a file inside an attached file.
    ...(raw.inline === true ? { inline: true } : {}),
    ...(wholeFrom(raw.child, 1, 99) != null ? { child: wholeFrom(raw.child, 1, 99) } : {}),
  };
}

/** A whole number from `min` to `max`, else null. */
function wholeFrom(raw: unknown, min: number, max: number): number | null {
  return typeof raw === "number" && Number.isInteger(raw) && raw >= min && raw <= max ? raw : null;
}

const LEAD_SOURCE_KINDS = [
  "trade_agreement",
  "purchase",
  "last_quoted",
  "mail_offer",
  "item_master_sales",
  "item_master_purchase",
  "item_master_inventory",
] as const;
type LeadSourceKind = (typeof LEAD_SOURCE_KINDS)[number];

/** Where a line's lead time comes from; null when the server sent none. */
function toLeadSource(raw: unknown): LinePrice["leadSource"] {
  if (!isRecord(raw) || !LEAD_SOURCE_KINDS.includes(raw.kind as LeadSourceKind)) {
    return null;
  }
  return {
    kind: raw.kind as LeadSourceKind,
    text: text(raw.text),
    docNumber: text(raw.doc_number),
    quotedAt: text(raw.quoted_at),
  };
}

const PRICE_SOURCES = ["engine", "sales"] as const;
const PRICE_APPROVALS = ["approved", "stale"] as const;
const LEAD_STATUSES = ["not_looked_up", "none_in_fo", "current", "historic"] as const;

/** The price block of a line; null when the server sent no price status. */
function toLinePrice(line: LiveQuoteLine): LinePrice | null {
  const status = oneOf(PRICE_STATUSES, line.price_status);
  if (!status) {
    return null;
  }
  return {
    status,
    source: oneOf(PRICE_SOURCES, line.price_source),
    setBy: text(line.price_set_by),
    setAt: text(line.price_set_at),
    approval: oneOf(PRICE_APPROVALS, line.price_approval),
    approvedBy: text(line.price_approved_by),
    approvedAt: text(line.price_approved_at),
    unitPrice: decimal(line.unit_price),
    currency: text(line.currency),
    group: text(line.price_group),
    unitQty: num(line.price_unit_qty),
    uom: text(line.price_uom),
    fromQty: num(line.price_from_qty),
    validTo: text(line.price_valid_to),
    expiredOn: text(line.price_expired_on),
    leadDays: num(line.lead_time_days),
    leadStatus: oneOf(LEAD_STATUSES, line.lead_time_status),
    leadSource: toLeadSource(line.lead_time_source),
    margin: oneOf(MARGIN_STATUSES, line.margin_status),
    marginBandText: text(line.margin_band_text),
    marginApprover: text(line.margin_approver),
    priceBasisText: text(line.price_basis_text),
    referenceText: text(line.reference_text),
    itemStatus: oneOf(ITEM_STATUSES, line.item_status),
    replacement: text(line.replacement_item),
    unitCost: decimal(line.unit_cost),
    contributionRatio: decimal(line.contribution_ratio),
    marginThreshold: decimal(line.margin_threshold),
    marginNote: text(line.margin_note),
    previousUnitPrice: decimal(line.previous_unit_price),
    previousCurrency: text(line.previous_currency),
  };
}

function toAttachmentsRead(raw: unknown): AttachmentRead[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (entry): entry is AttachmentRead =>
        isRecord(entry) &&
        typeof entry.file === "string" &&
        typeof entry.index === "number" &&
        Number.isInteger(entry.index) &&
        entry.index >= 0,
    )
    .map((entry) => ({
      ...entry,
      lines_read: toCount(entry.lines_read),
      handwriting: entry.handwriting === true,
      lines_dropped: toCount(entry.lines_dropped),
      photo: toPhotoReading(entry.photo),
      filter_category: text(entry.filter_category),
      inline: entry.inline === true,
      parent_index: wholeFrom(entry.parent_index, 0, 999),
      child: wholeFrom(entry.child, 1, 99),
      pictures_inside: toCount(entry.pictures_inside),
    }));
}

function toOrderCheck(raw: unknown): OrderCheck | null {
  if (!isRecord(raw)) {
    return null;
  }
  const check = raw as Partial<OrderCheck>;
  return {
    quotation: text(check.quotation),
    coverage: oneOf(ORDER_COVERAGES, check.coverage) ?? "none",
    reference: check.reference ?? null,
    reference_date: text(check.reference_date),
    coverage_text: text(check.coverage_text) ?? "",
    header_flags: (Array.isArray(check.header_flags) ? check.header_flags : []).filter(
      (flag) => isRecord(flag) && typeof flag.text === "string" && flag.text.trim() !== "",
    ),
    lines: (Array.isArray(check.lines) ? check.lines : []).filter(
      (line) => isRecord(line) && typeof line.line_index === "number",
    ),
    not_ordered: (Array.isArray(check.not_ordered) ? check.not_ordered : []).filter(
      (line) => isRecord(line) && typeof line.item === "string",
    ),
  };
}

/**
 * True when the customer block says something only the stage 2 engine writes. The
 * API fills the new keys with defaults (false, empty list) for older drafts too, so
 * only a value that carries information counts.
 */
function hasStage2Customer(raw: Record<string, unknown>): boolean {
  return (
    text(raw.role) !== null ||
    text(raw.basis) !== null ||
    raw.suggested === true ||
    raw.confirmed === true ||
    isRecord(raw.agent) ||
    isRecord(raw.end_customer) ||
    isRecord(raw.intercompany) ||
    (Array.isArray(raw.evidence) && raw.evidence.length > 0) ||
    (Array.isArray(raw.candidates) && raw.candidates.length > 0)
  );
}

/** The sister company that issued the order; null without a printed issuer name. */
function toIntercompany(raw: unknown): CustomerIntercompany | null {
  if (!isRecord(raw)) {
    return null;
  }
  const buyerName = text(raw.buyer_name);
  if (!buyerName) {
    return null;
  }
  const doc = isRecord(raw.document) ? raw.document : null;
  const index = doc ? num(doc.index) : null;
  // Stage 4: only present when the server proved the mail is from inside Norican.
  const proof = oneOf(INTERCOMPANY_PROOFS, raw.proof);
  return {
    buyer_key: text(raw.buyer_key) ?? undefined,
    buyer_name: buyerName.slice(0, MAX_BUYER_NAME_CHARS),
    document: doc
      ? {
          index: index != null && Number.isInteger(index) && index >= 0 ? index : null,
          file: text(doc.file),
          doc_number: text(doc.doc_number),
        }
      : null,
    ...(proof ? { proof } : {}),
  };
}

/** The evidence sentences of the customer: at most four, each cut at 160 characters. */
function toCustomerEvidence(raw: unknown): CustomerEvidence[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((entry): entry is CustomerEvidence => isRecord(entry) && text(entry.text) !== null)
    .slice(0, MAX_CUSTOMER_EVIDENCE)
    .map((entry) => ({
      kind: text(entry.kind) ?? undefined,
      text: (text(entry.text) as string).slice(0, MAX_EVIDENCE_CHARS),
    }));
}

/**
 * The customer block for the customer card; null for a case written before stage 2
 * that has no work row either (nothing to show beyond what the header already says).
 * The similarity figure is dropped here so it can never be printed.
 */
function toCustomerBlock(raw: unknown, work: CaseWork | null): CustomerBlock | null {
  const block = isRecord(raw) ? raw : {};
  if (!hasStage2Customer(block) && !work) {
    return null;
  }
  const party = (value: unknown) =>
    isRecord(value) && text(value.name)
      ? { account: text(value.account), name: text(value.name) as string }
      : null;
  const endCustomer = party(block.end_customer);
  const candidates = (Array.isArray(block.candidates) ? block.candidates : [])
    .filter((c): c is CustomerCandidate => isRecord(c) && text(c.account) !== null)
    .slice(0, MAX_CUSTOMER_CANDIDATES)
    .map((c) => ({
      account: (c.account as string).trim(),
      name: text(c.name) ?? "",
      kind: oneOf(CANDIDATE_KINDS, c.kind) ?? undefined,
      basis: text(c.basis) ?? undefined,
      city: text(c.city),
      postcode: text(c.postcode),
      invoice_account: text(c.invoice_account),
      source_evidence: toMatchingSources(c.source_evidence),
    }));
  return {
    account: text(block.account),
    confident: block.confident === true,
    how: text(block.how) ?? undefined,
    via: text(block.via),
    entity: text(block.entity),
    name: text(block.name),
    company: text(block.company),
    role: oneOf(CUSTOMER_ROLES, block.role) ?? "unknown",
    suggested: block.suggested === true,
    basis: text(block.basis) ?? undefined,
    forwarded_from: text(block.forwarded_from),
    portal: text(block.portal),
    agent: party(block.agent),
    end_customer: endCustomer
      ? {
          ...endCustomer,
          evidence: text((block.end_customer as Record<string, unknown>).evidence) ?? "",
        }
      : null,
    candidates,
    confirmed: block.confirmed === true,
    intercompany: toIntercompany(block.intercompany),
    evidence: toCustomerEvidence(block.evidence),
    question: text(block.question),
    source_evidence: toMatchingSources(block.source_evidence),
    quote_first: oneOf(QUOTE_FIRST, block.quote_first),
    instruction: text(block.instruction)?.slice(0, MAX_INSTRUCTION_CHARS) ?? null,
  };
}

/** Hunt round 2, decision 2: the party whose account is quoted first on an agent's mail. */
const QUOTE_FIRST = ["agent", "end_customer"] as const;
/** Hunt round 2 (Q-18): the instruction copied from the mail is at most 300 characters. */
const MAX_INSTRUCTION_CHARS = 300;

/** A non-negative whole number from an untrusted wire value (anything else is 0). */
function toMatchingSources(raw: unknown): MatchingSourceEvidence[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((entry) => isRecord(entry) && text(entry.label))
    .slice(0, 10)
    .map((entry) => ({
      label: (text(entry.label) ?? "").slice(0, 160),
      page:
        typeof entry.page === "number" && Number.isInteger(entry.page) && entry.page > 0
          ? entry.page
          : null,
      row:
        typeof entry.row === "number" && Number.isInteger(entry.row) && entry.row > 0
          ? entry.row
          : null,
      sheet: text(entry.sheet)?.slice(0, 100) ?? null,
    }));
}

function toMatchingReview(raw: unknown): MatchingInvestigationSummary | null {
  if (!isRecord(raw)) {
    return null;
  }
  return {
    questions: (Array.isArray(raw.questions) ? raw.questions : [])
      .filter((q): q is string => typeof q === "string" && q.trim() !== "")
      .slice(0, 5)
      .map((q) => q.slice(0, 300)),
    sources: toMatchingSources(raw.sources),
  };
}

function toCount(raw: unknown): number {
  return typeof raw === "number" && Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
}

// ─── the deeper search (round 2, 13 Oct 2026) ────────────────────────────────

const PROPOSAL_STATUSES = ["proposed", "downgraded", "unverified"] as const;
const PROPOSAL_ROLES = ["pick", "alternative"] as const;
const PROPOSAL_DECISIONS = ["confirmed", "rejected"] as const;
const DISCOUNT_BASES = ["item_last", "account_usual"] as const;
const MAX_TRAIL = 6;
const MAX_TRAIL_ROWS = 6;
// The backend's case cap (`mail.attachments.CASE_LINES_MAX`, `InvestigationView.lines`):
// a long list keeps a proposal on every line (decision 4; review of round 2, finding 16).
const MAX_PROPOSAL_LINES = 500;

/** One lookup of the trail: only its name, its query and its row labels cross the seam. */
function toInvestigationSteps(raw: unknown): InvestigationStep[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter((entry) => isRecord(entry) && text(entry.lookup))
    .slice(0, MAX_TRAIL)
    .map((entry) => ({
      lookup: (text(entry.lookup) ?? "").slice(0, 60),
      query: (text(entry.query) ?? "").slice(0, 160),
      rows: (Array.isArray(entry.rows) ? entry.rows : [])
        .filter((row: unknown): row is string => typeof row === "string" && row.trim() !== "")
        .slice(0, MAX_TRAIL_ROWS)
        .map((row: string) => row.trim().slice(0, 160)),
      decided: entry.decided === true,
    }));
}

/** A proposal of the deeper search; null when it has no id, no status or nothing to propose. */
function toProposal(raw: unknown, kind: "customer" | "item"): InvestigationProposal | null {
  if (!isRecord(raw) || raw.kind !== kind) {
    return null;
  }
  const id = text(raw.proposal_id);
  const status = oneOf(PROPOSAL_STATUSES, raw.status);
  const account = kind === "customer" ? text(raw.account) : null;
  const item = kind === "item" ? text(raw.item) : null;
  if (!id || !status || (kind === "customer" ? !account : !item)) {
    return null;
  }
  return {
    id,
    kind,
    lineKey: kind === "item" ? writableLineKey(raw.line_key) : null,
    account,
    item,
    name: text(raw.name) ?? "",
    status,
    role: oneOf(PROPOSAL_ROLES, raw.role) ?? "pick",
    reason: (text(raw.reason) ?? "").slice(0, 300),
    doubt: text(raw.doubt)?.slice(0, 300) ?? null,
    question: text(raw.question)?.slice(0, 300) ?? null,
    signals: (Array.isArray(raw.signals) ? raw.signals : [])
      .filter((signal: unknown): signal is string => typeof signal === "string" && signal !== "")
      .slice(0, 8),
    evidence: toInvestigationSteps(raw.evidence),
    decision: oneOf(PROPOSAL_DECISIONS, raw.decision),
  };
}

/** The deeper search of a case as the page reads it; null when the server sent none. */
export function toInvestigation(raw: unknown): CaseInvestigation | null {
  if (!isRecord(raw)) {
    return null;
  }
  const revision = isRecord(raw.revision) && text(raw.revision.quotation) ? raw.revision : null;
  return {
    status: text(raw.status) ?? "complete",
    onDemand: raw.on_demand === true,
    pending: raw.pending === true,
    lookAgainAllowed: raw.look_again_allowed === true,
    customer: toProposal(raw.customer, "customer"),
    otherAccount: toProposal(raw.other_account, "customer"),
    lines: (Array.isArray(raw.lines) ? raw.lines : [])
      .map((entry) => toProposal(entry, "item"))
      .filter((entry): entry is InvestigationProposal => entry !== null)
      .slice(0, MAX_PROPOSAL_LINES),
    questions: (Array.isArray(raw.questions) ? raw.questions : [])
      .filter((q): q is string => typeof q === "string" && q.trim() !== "")
      .slice(0, 5)
      .map((q) => q.slice(0, 300)),
    provisionalAccount: text(raw.provisional_account),
    provisionalBasis: text(raw.provisional_basis),
    revision: revision
      ? {
          quotation: text(revision.quotation) ?? "",
          kind: text(revision.kind) ?? "",
          kept: toCount(revision.kept),
          removed: toCount(revision.removed),
          changed: toCount(revision.changed),
          added: toCount(revision.added),
        }
      : null,
  };
}

/** The provisional values of a line; null without an account or without any value. */
export function toProvisional(raw: unknown): LineProvisional | null {
  if (!isRecord(raw)) {
    return null;
  }
  const account = text(raw.account);
  const provisional: LineProvisional = {
    account: account ?? "",
    unitPrice: decimal(raw.unit_price),
    currency: text(raw.currency),
    unitQty: num(raw.price_unit_qty),
    uom: text(raw.uom),
    group: text(raw.price_group),
    validTo: text(raw.valid_to),
    sharedByCandidates: raw.shared_by_candidates === true,
    qtyAssumed: raw.qty_assumed === 1,
    discountPct: decimal(raw.discount_pct),
    discountBasis: oneOf(DISCOUNT_BASES, raw.discount_basis),
    lastUnitPrice: decimal(raw.last_unit_price),
    lastQuotedOn: text(raw.last_quoted_on),
    leadDays: num(raw.lead_days),
    leadSource: text(raw.lead_source),
  };
  const empty =
    provisional.unitPrice == null &&
    provisional.discountPct == null &&
    provisional.lastUnitPrice == null &&
    provisional.leadDays == null;
  return account && !empty ? provisional : null;
}

/** The items the engine offers for a line: entries without an item number are dropped. */
function toSuggestions(raw: unknown): LineCandidate[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (entry): entry is LineCandidate =>
        entry != null &&
        typeof entry === "object" &&
        typeof (entry as LineCandidate).item === "string" &&
        (entry as LineCandidate).item.trim() !== "",
    )
    .slice(0, MAX_SUGGESTIONS)
    .map((entry) => ({
      item: entry.item.trim(),
      name: typeof entry.name === "string" && entry.name.trim() !== "" ? entry.name.trim() : null,
      area: typeof entry.area === "string" && entry.area.trim() !== "" ? entry.area.trim() : null,
    }));
}

/** The thread and the copies of a case: entries without an id or a known relation are dropped. */
function toRelated(raw: unknown): RelatedCase[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (entry): entry is RelatedCase =>
      entry != null &&
      typeof entry === "object" &&
      typeof (entry as RelatedCase).id === "string" &&
      oneOf(RELATIONS, (entry as RelatedCase).relation) !== null,
  );
}

/**
 * The colleagues' replies filed on a case (D1, 19 Oct 2026). A note without a case id is
 * dropped; a number without its written form is dropped; the excerpt is capped at 300
 * characters, as the contract says.
 */
function toColleagueNotes(raw: unknown): ColleagueNote[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(isRecord).flatMap((entry) => {
    const caseId = text(entry.case_id);
    if (!caseId) {
      return [];
    }
    const numbers = (Array.isArray(entry.numbers) ? entry.numbers : [])
      .filter(isRecord)
      .flatMap((number) => {
        const written = text(number.written);
        return written
          ? [
              {
                written,
                item: text(number.item),
                name: text(number.name),
                line: wholeFrom(number.line, 1, 999),
              },
            ]
          : [];
      })
      .slice(0, 10);
    const excerpt = text(entry.excerpt);
    return [
      {
        case_id: caseId,
        received_at: text(entry.received_at) ?? "",
        sender_name: text(entry.sender_name),
        sender_email: text(entry.sender_email),
        numbers,
        excerpt: excerpt && excerpt.length > 300 ? `${excerpt.slice(0, 299)}…` : excerpt,
      },
    ];
  });
}

/** The quotations linked to a case: an unknown source is dropped, an unknown status reads "unknown". */
function toQuotations(raw: unknown): CaseQuotation[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(
      (entry): entry is CaseQuotation =>
        entry != null &&
        typeof entry === "object" &&
        typeof (entry as CaseQuotation).number === "string" &&
        oneOf(QUOTATION_SOURCES, (entry as CaseQuotation).source) !== null,
    )
    .map((entry) => ({ ...entry, status: oneOf(QUOTATION_STATUSES, entry.status) ?? "unknown" }));
}

/** The line's status; an older payload without `status` is derived from `auto_accept`. */
function toLineStatus(line: LiveQuoteLine): LineStatus {
  const status = oneOf(LINE_STATUSES, line.status);
  if (status) {
    return status;
  }
  if (line.auto_accept) {
    return "matched";
  }
  return line.outcome === "match" && line.item ? "suggested" : "open";
}

/** Line state for the older surfaces (add-in strip): matched = certified, suggested = assisted. */
function toLineState(status: LineStatus): LineState {
  if (status === "matched") {
    return "certified";
  }
  return status === "suggested" ? "assisted" : "review";
}

/**
 * The model's own bookkeeping removed from a "why" text: "candidate 4", "index 2" and
 * "routed to review ..." mean nothing to sales (360 simulation P05).
 */
export function cleanWhyText(why: string): string {
  return why
    .replace(/\s*\(?\b(?:candidate|index)\s*#?\d+\)?/gi, "")
    .replace(/[,;.]?\s*routed to (?:the )?review\b[^.;]*[.;]?/gi, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.;:])/g, "$1")
    .trim();
}

/**
 * The reason shown to the reviewer. The server already cleans it; this is the safety
 * net for an older payload: no "[salesperson]" prefix and never raw error text.
 */
function toLineReason(line: LiveQuoteLine, status: LineStatus): string {
  const why = cleanWhyText((line.why ?? "").replace(/^\s*\[salesperson\]\s*/i, ""));
  if (status === "failed" || RAW_ERROR.test(why)) {
    return LINE_FAILED_SENTENCE;
  }
  return why;
}

/**
 * A stored draft line → the UI line. The stored draft keeps status + reason but no
 * per-line confidence, ladder level or evidence chain, so `confidenceKnown: false`
 * tells the review page not to print a number it does not have.
 */
function toUiLine(caseId: string, line: LiveQuoteLine, index: number): QuoteLine {
  const item = line.item ?? null;
  const reason = toLineReason(line, toLineStatus(line));
  const source = toLineSource(line.source);
  const reasonCode = oneOf(REASON_CODES, line.reason_code);
  const override = toLineOverride(line.override);
  // A price a person set sits in the line's price fields; the evidence names what I2Q
  // found (the list price behind the override), never the person's price as a list price.
  const manualPrice = line.price_source === "sales";
  const listPrice = manualPrice ? (override?.engine_unit_price ?? null) : line.unit_price;
  const listCurrency = manualPrice ? (override?.engine_currency ?? null) : line.currency;
  let status: LineStatus = reason === LINE_FAILED_SENTENCE ? "failed" : toLineStatus(line);
  // A line read from a scanned page or a photo is never shown as matched: a person confirms it.
  // Hunt round 2: an item that does not fit the row (Q-09, Q-10), or a scan line one
  // reading gave, is never one-click either: the bulk accept skips it.
  const mismatch = oneOf(MISMATCHES, line.mismatch);
  const readOnce = line.read_once === true;
  if (
    status === "matched" &&
    (isScanSource(source) ||
      reasonCode === "scan_read" ||
      reasonCode === "photo_read" ||
      mismatch !== null ||
      readOnce)
  ) {
    status = item ? "suggested" : "open";
  }
  return {
    id: `${caseId}-${index + 1}`,
    enquiryText: line.enquiry_line,
    matchedItemId: item,
    qty: line.qty ?? null,
    qtyEarlierMail: line.qty_source === "earlier_mail",
    qtyDefault: line.qty_source === "default",
    state: toLineState(status),
    status,
    itemName: line.item_name ?? null,
    fallback: line.fallback ?? false,
    catalogueArea:
      typeof line.catalogue_area === "string" && line.catalogue_area.trim() !== ""
        ? line.catalogue_area.trim().toLowerCase()
        : null,
    isSpare: typeof line.is_spare === "boolean" ? line.is_spare : null,
    reasonCode,
    requestAction: oneOf(["add", "remove", "replace", "reference"] as const, line.request_action),
    matchBasis: text(line.match_basis),
    suggestions: toSuggestions(line.candidates),
    sourceEvidence: toMatchingSources(line.source_evidence),
    resolution: {
      outcome: line.outcome,
      item_id: item,
      ladder_level: 0,
      confidence: 0,
      auto_accept: status === "matched",
      verdict: line.outcome,
      reason,
      // Why this item (spare-part sources), then the price chip in words with the amount
      // formatted; never the chip code or the raw decimal.
      evidence: [
        ...toLineEvidence(line.evidence),
        ...(line.price_chip && !(manualPrice && listPrice == null)
          ? [
              {
                kind: "price",
                detail: priceEvidenceText(line.price_chip, listPrice, listCurrency),
              },
            ]
          : []),
        // The person's price as its own line, named as such (tester round 5).
        ...(manualPrice
          ? [{ kind: "price", detail: manualPriceEvidenceText(line.unit_price, line.currency) }]
          : []),
      ],
    },
    confidenceKnown: false,
    priceChip: line.price_chip ?? null,
    // An orphan's key (hunt round 3, H3) is never a line key the page writes on.
    lineKey: writableLineKey(line.line_key),
    decision: toDecision(line.decision),
    twinDecision: toTwinDecision(line.twin_decision),
    override,
    source,
    unit: text(line.unit),
    qtyIssue: text(line.qty_issue),
    priceQty: num(line.price_qty),
    qtyConversion: text(line.qty_conversion),
    customerItem: text(line.customer_item),
    poUnitPrice: num(line.po_unit_price),
    poCurrency: text(line.po_currency),
    needBy: text(line.need_by),
    fromPartsList: line.from_parts_list === true,
    price: toLinePrice(line),
    quoteHistory: toQuoteHistory(line.quote_history),
    quoteDelta: oneOf(QUOTE_DELTAS, line.quote_delta),
    qtyBefore: typeof line.qty_before === "number" ? line.qty_before : null,
    provisional: toProvisional(line.provisional),
    otherEntity: toOtherEntity(line.other_entity_items),
    mismatch,
    readOnce,
    hsnCode: text(line.hsn_code),
    hsnStatus: oneOf(HSN_STATUSES, line.hsn_status),
  };
}

/**
 * The other-entity hints of a line (hunt round 2, decision 5): at most three, each with an
 * entity code and an item; anything else is dropped.
 */
function toOtherEntity(raw: unknown): OtherEntityItem[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .filter(isRecord)
    .map((entry) => ({
      entity: text(entry.entity)?.toLowerCase() ?? "",
      item: text(entry.item) ?? "",
      name: text(entry.name),
    }))
    .filter((entry) => /^[a-z]{2,4}$/.test(entry.entity) && entry.item !== "")
    .slice(0, MAX_OTHER_ENTITY);
}

const QUOTE_DELTAS = ["remove", "qty", "keep"] as const;
// `order`: decision 1 of 14 Oct 2026 (an order or a signed copy of our quotation).
const FOLLOW_UP_KINDS = ["amend", "question", "other", "price", "order"] as const;
const FOLLOW_UP_TOPICS = ["freight", "lead_time", "price", "completeness"] as const;

/** The follow-up on our quotation (sim360 P34); null when absent or malformed. */
export function toQuoteFollowUp(raw: unknown): QuoteFollowUp | null {
  if (!isRecord(raw) || typeof raw.number !== "string" || typeof raw.sentence !== "string") {
    return null;
  }
  const topics = Array.isArray(raw.topics)
    ? raw.topics
        .map((topic) => oneOf(FOLLOW_UP_TOPICS, topic))
        .filter((topic): topic is (typeof FOLLOW_UP_TOPICS)[number] => topic !== null)
    : [];
  return {
    number: raw.number,
    kind: oneOf(FOLLOW_UP_KINDS, raw.kind) ?? "other",
    topics,
    sentence: raw.sentence,
    original_case_id: text(raw.original_case_id),
    // Decision 1 of 14 Oct 2026: the customer's order number, only when the server sent one.
    ...(text(raw.order_number) ? { order_number: text(raw.order_number) } : {}),
    // Hunt round 2, decision 7: every quotation of ours the mail names, `number` first.
    numbers: [
      ...new Set(
        [raw.number, ...(Array.isArray(raw.numbers) ? raw.numbers : [])]
          .map((entry) => text(entry))
          .filter((entry): entry is string => entry !== null),
      ),
    ],
  };
}

/** Why the engine proposes a line's item; malformed entries are dropped. */
function toLineEvidence(raw: unknown): { kind: string; detail: string }[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const out: { kind: string; detail: string }[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) {
      continue;
    }
    const kind = text(entry.kind);
    const detail = text(entry.detail);
    if (kind && detail) {
      out.push({ kind, detail });
    }
  }
  return out;
}

/** The customer's last F&O quotation of a line's item; null unless the wire sent one. */
function toQuoteHistory(raw: unknown): QuoteHistoryEvidence | null {
  if (!isRecord(raw) || typeof raw.last_quotation_id !== "string" || raw.last_quotation_id === "") {
    return null;
  }
  return raw as QuoteHistoryEvidence;
}

/** The header values of the customer's latest F&O quotations (well-formed rows only). */
function toCustomerHeader(raw: unknown): CustomerHeaderDefault[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter(
    (row): row is CustomerHeaderDefault =>
      isRecord(row) &&
      typeof row.field === "string" &&
      typeof row.value === "string" &&
      isRecord(row.source) &&
      typeof row.source.ref === "string",
  );
}

/** True for a line that was read from a scanned page (stage 3). */
export function isScanSource(source: LineSource | null | undefined): boolean {
  return source != null && source.kind === "attachment" && source.method === "image";
}

/** `engine_error` as shown: the server's fixed sentence, never raw error text. */
function toEngineError(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }
  return raw.length > 120 || /error|exception|traceback/i.test(raw) ? ENGINE_ERROR_SENTENCE : raw;
}

/**
 * Lines read from a loose picture (not a scanned PDF) are tagged as read from a photo, and
 * their source learns how many parts the picture was cut into ("part 2 of 4").
 */
function withPhotoSources(lines: QuoteLine[], attachments: AttachmentRead[]): QuoteLine[] {
  return lines.map((line) => {
    const source = line.source;
    if (!isScanSource(source) || source?.kind !== "attachment") {
      return line;
    }
    const attachment = attachments.find((entry) => entry.index === source.index);
    const fromPhoto = isPictureAttachment(attachment) || line.reasonCode === "photo_read";
    const parts = source.parts ?? attachment?.photo?.parts ?? null;
    return { ...line, fromPhoto, source: { ...source, parts } };
  });
}

/** The code as compared between a picture and a line: upper case, no whitespace. */
function codeKey(raw: unknown): string {
  return typeof raw === "string" ? raw.replace(/\s+/g, "").toUpperCase() : "";
}

/**
 * Which draft lines each picture fed, when the server did not say: the lines whose source
 * is the picture, and for each item the line that carries its code (`photo.read_as`, else
 * the item). A server value always wins, and a row marked `not_asked` keeps no line.
 */
function linkPhotoLines(
  attachments: AttachmentRead[],
  rawLines: LiveQuoteLine[],
): AttachmentRead[] {
  const sourceIndex = (line: LiveQuoteLine): number | null => {
    const source = isRecord(line.source) ? line.source : null;
    return source?.kind === "attachment" && typeof source.index === "number" ? source.index : null;
  };
  const readAs = (line: LiveQuoteLine): string => {
    const photo = (line as { photo?: unknown }).photo;
    return codeKey(
      isRecord(photo) && typeof photo.read_as === "string" ? photo.read_as : line.item,
    );
  };
  return attachments.map((attachment) => {
    const photo = attachment.photo;
    if (!photo) {
      return attachment;
    }
    const fed = rawLines
      .map((line, i) => (sourceIndex(line) === attachment.index ? i + 1 : null))
      .filter((n): n is number => n !== null);
    const items = photo.items.map((item) => {
      // A row the mail does not ask for (D2) is context: it never gets a line here.
      if (item.line != null || !item.code || item.use === "not_asked") {
        return item;
      }
      const key = codeKey(item.code);
      const i = rawLines.findIndex(
        (line) => sourceIndex(line) === attachment.index && readAs(line) === key,
      );
      const line = rawLines[i];
      if (!line) {
        return item;
      }
      const use = line.item
        ? "suggestion"
        : (line.candidates ?? []).length > 0
          ? "candidates"
          : "needs_pick";
      return { ...item, line: i + 1, use: item.use ?? use };
    });
    return {
      ...attachment,
      photo: { ...photo, items, fed_lines: photo.fed_lines.length > 0 ? photo.fed_lines : fed },
    };
  });
}

function toUiDetail(raw: LiveQuoteCaseDetail): QuoteCaseDetail {
  const summary = toUiSummary(raw);
  const attachmentsRead = linkPhotoLines(
    toAttachmentsRead(raw.attachments_read),
    Array.isArray(raw.lines) ? raw.lines : [],
  );
  const work = toWork(raw.work);
  const thread: MailSegment[] = (raw.thread ?? []).map((segment) => ({
    from: segment.from,
    when: formatWhen(segment.when),
    body: segment.body ?? "",
    quoted: segment.quoted ?? false,
  }));
  const preflight: PreflightChip[] = (raw.preflight ?? []).map((chip) => ({
    label: chip.label,
    tone: toTone(chip.tone, "info"),
  }));
  return {
    ...summary,
    slaMinutesLeft: raw.sla_minutes_left ?? null,
    customerResolvedConfidence:
      raw.customer_resolved_confidence == null
        ? null
        : toConfidencePercent(raw.customer_resolved_confidence),
    incoterms: raw.incoterms ?? null,
    needBy: raw.need_by ?? null,
    autonomyLevel: raw.autonomy_level ?? null,
    thread,
    fields: (raw.fields ?? []).map((field) => ({
      label: field.label,
      value: field.value,
      tone: toFieldTone(field.tone),
    })),
    lines: withPhotoSources(
      (raw.lines ?? []).map((line, index) => toUiLine(raw.id, line, index)),
      attachmentsRead,
    ),
    preflight,
    outcome: raw.outcome ?? null,
    engineError: toEngineError(raw.engine_error),
    linesExtracted: raw.lines_extracted ?? true,
    whyHere: (raw.why_here ?? []).filter((s): s is string => typeof s === "string" && s !== ""),
    customerAccount: toConfidentAccount(raw.customer_resolution),
    related: toRelated(raw.related),
    quotations: toQuotations(raw.quotations),
    // "Quoted in F&O" (20 Oct 2026): the link of the GROUND TRUTH stage, passed through.
    groundTruth: toGroundTruthField(raw.ground_truth),
    work,
    closedBy: toClosedBy(raw.closed_by),
    earlierDecisions: toDecisions(raw.earlier_decisions),
    earlierMentions: toEarlierMentions(raw.earlier_mentions),
    attachmentsRead,
    orderCheck: toOrderCheck(raw.order_check),
    customerBlock: toCustomerBlock(raw.customer_resolution, work),
    matchingReview: toMatchingReview(raw.matching_review),
    investigation: toInvestigation(raw.investigation),
    model: typeof raw.model === "string" && raw.model.trim() !== "" ? raw.model : null,
    olderResult: text(raw.older_result),
    quoteFollowUp: toQuoteFollowUp(raw.quote_follow_up),
    customerCertainty: toCustomerCertainty(raw.customer_resolution, work),
    rules: toCaseRules(raw.rules),
    customerHeader: toCustomerHeader(raw.customer_header),
    blockers: toBlockers(raw.blockers),
    colleagueNotes: toColleagueNotes(raw.colleague_notes),
  };
}

/**
 * What sales quoted in F&O for the case (`ground_truth`, 20 Oct 2026), passed through.
 * Null when the wire has none, or when it lacks the two keys every link has (the source
 * and the certainty), so the block of the case page never shows half a link.
 */
function toGroundTruthField(raw: unknown): CaseGroundTruth | null {
  if (!isRecord(raw) || typeof raw.source !== "string" || typeof raw.certainty !== "string") {
    return null;
  }
  return raw as unknown as CaseGroundTruth;
}

/**
 * What stands between the case and a reply, as the server lists it (passed through).
 * Null when the wire has none or the value is not a list: the page then shows its own
 * captions. An entry without a kind and a level is dropped.
 */
function toBlockers(raw: unknown): CaseBlocker[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  return raw.filter(
    (entry): entry is CaseBlocker =>
      isRecord(entry) && typeof entry.kind === "string" && typeof entry.level === "string",
  );
}

/**
 * How sure the case is about its customer, for the header line: no block at all,
 * identified with confidence (or confirmed by a rep), or a name that still needs confirming.
 */
function toCustomerCertainty(
  raw: LiveQuoteCaseDetail["customer_resolution"],
  work: CaseWork | null,
): "none" | "certain" | "to-confirm" {
  if (work?.customer_account) {
    return "certain";
  }
  if (raw == null || !isRecord(raw)) {
    return "none";
  }
  return raw.confident === true || raw.confirmed === true ? "certain" : "to-confirm";
}

/** The customer account of the case, only when it was identified with confidence. */
function toConfidentAccount(raw: LiveQuoteCaseDetail["customer_resolution"]): string | null {
  if (raw == null || raw.confident !== true || typeof raw.account !== "string") {
    return null;
  }
  return raw.account.trim() || null;
}

/** Untrusted `/queue` payload → UI rows (rows without a string `id` are dropped). */
export function toLiveSummaries(raw: unknown): QuoteCaseSummary[] | null {
  if (!Array.isArray(raw)) {
    return null;
  }
  return raw
    .filter(
      (item): item is WireSummary =>
        item != null && typeof item === "object" && typeof (item as WireSummary).id === "string",
    )
    .map(toUiSummary);
}

/** Untrusted case payload → UI detail, or null when it is not a usable case. */
export function toLiveDetail(raw: unknown): QuoteCaseDetail | null {
  if (raw == null || typeof raw !== "object") {
    return null;
  }
  const detail = raw as LiveQuoteCaseDetail;
  if (typeof detail.id !== "string") {
    return null;
  }
  return toUiDetail(detail);
}

// ─── seam hooks ─────────────────────────────────────────────────────────────

/** The queue seam's result: one page of rows plus the server totals. */
export interface QueueData extends WorkbenchData<QuoteCaseSummary[]> {
  /** Rows matching every filter including the view (across all pages). */
  total: number;
  /** Per-view counts under the same filters; null when the source has none. */
  counts: QueueCounts | null;
  /** Open inquiries the date filter hides; null when the source has none. */
  backlog: QueueBacklog | null;
  /** True while a new page / filter is loading and the previous rows are still shown. */
  isRefreshing: boolean;
}

/**
 * One page of the case queue, server-filtered and server-paged.
 *
 * Production: the live page, `[]` for an empty result (the page's empty state), or
 * `isError` (the page's error state). Dev: the same, except that a failing store
 * falls back to the fixtures so local work has something to show.
 */
export function useQueueCases(filters: QueueFilters = {}): QueueData {
  const query = useQueue(filters);
  const rows = toLiveSummaries(query.data?.items);
  const base = { updatedAt: query.dataUpdatedAt, error: query.error };
  const isRefreshing = query.isPlaceholderData && query.isFetching;

  if (rows && query.data) {
    return {
      ...base,
      data: rows,
      total: query.data.total,
      counts: query.data.counts,
      backlog: query.data.backlog,
      isLoading: false,
      isError: false,
      isLive: true,
      isRefreshing,
    };
  }
  if (DEV_FIXTURES && !query.isLoading) {
    const fixtures = [...(toLiveSummaries(getWireQueue()) ?? []), ...getQueue()];
    return {
      ...base,
      data: fixtures,
      total: fixtures.length,
      counts: null,
      backlog: null,
      isLoading: false,
      isError: false,
      isLive: false,
      isRefreshing: false,
    };
  }
  return {
    ...base,
    data: [],
    total: 0,
    counts: null,
    backlog: null,
    isLoading: query.isLoading,
    isError: query.isError,
    isLive: false,
    isRefreshing: false,
  };
}

/**
 * One full case for the 3-pane review, or `undefined` when the store does not
 * know the id (the page's "no case" state). Dev falls back to the fixture.
 */
export function useCaseDetail(id: string): WorkbenchData<QuoteCaseDetail | undefined> {
  const query = useCase(id);
  const detail = toLiveDetail(query.data);
  const base = { updatedAt: query.dataUpdatedAt, error: query.error };

  if (detail) {
    return { ...base, data: detail, isLoading: false, isError: false, isLive: true };
  }
  if (DEV_FIXTURES && !query.isLoading) {
    // Stage 2 fixtures are kept in the wire shape and cross the same mapper as live data.
    const fixture = getCase(id) ?? toLiveDetail(getWireCase(id)) ?? undefined;
    return { ...base, data: fixture, isLoading: false, isError: false, isLive: false };
  }
  // A 404/422 for an unknown id is "no case", not a failure worth an error banner.
  const status = (query.error as { status?: number } | null)?.status;
  const notFound = status === 404 || status === 422;
  return {
    ...base,
    data: undefined,
    isLoading: query.isLoading && id.trim().length > 0,
    isError: query.isError && !notFound,
    isLive: false,
  };
}

/**
 * The stored case for an open Outlook message (by `internetMessageId`), or
 * `undefined` when it was never ingested. Live only — there is no fixture for a
 * real mailbox item, in dev either.
 */
export function useCaseForMessage(
  messageId: string | null | undefined,
): WorkbenchData<QuoteCaseDetail | undefined> {
  const query = useCaseByMessage(messageId);
  // Decisions and their expected version use the canonical case cache. The message
  // lookup supplies its identity; subscribing here keeps the pane current after writes.
  const caseId = query.data?.id ?? "";
  const current = useCase(caseId);
  const detail = toLiveDetail(current.data);
  return {
    data: detail ?? undefined,
    isLoading: query.isLoading || (caseId.length > 0 && current.isLoading),
    isError: query.isError || (caseId.length > 0 && current.isError),
    isLive: detail !== null,
    updatedAt: current.dataUpdatedAt || query.dataUpdatedAt,
    error: query.error ?? current.error,
  };
}

/** A live query plus the data a page renders (the dev fixture only in the dev server). */
export interface SeamQuery<T> {
  query: UseQueryResult<T | null | undefined>;
  data: T | undefined;
  /** False when the data is the local dev fixture. */
  isLive: boolean;
}

/**
 * Ops-dashboard metrics (`GET /metrics/ops`, by days or completed weeks). Production:
 * live or error. Dev: the fixture when the endpoint is not reachable.
 */
export function useDashboardMetrics(
  window: number | OpsWindow = 30,
  enabled = true,
): SeamQuery<OpsMetrics> {
  const query = useOpsMetrics(window, enabled);
  if (query.data) {
    return { query, data: query.data, isLive: true };
  }
  if (DEV_FIXTURES && query.isError) {
    return { query, data: getOpsMetrics(), isLive: false };
  }
  return { query, data: undefined, isLive: false };
}

/**
 * The executive summary or the monthly report (`GET /metrics/executive`, managers).
 * Production: live or error. Dev: an invented fixture when the endpoint is not there yet.
 */
export function useExecutiveData(window: ExecutiveWindow, enabled: boolean): SeamQuery<Executive> {
  const query = useExecutiveMetrics(window, enabled);
  if (query.data) {
    return { query, data: query.data, isLive: true };
  }
  if (DEV_FIXTURES && enabled && query.isError) {
    return { query, data: toExecutive(getExecutiveFixture(window)), isLive: false };
  }
  return { query, data: undefined, isLive: false };
}
